// Inline amount fields on the Budget tab: a planned amount, room for other spending, or an item's
// amount. Each saves when the user leaves the field or presses Enter, then shows "Saved" briefly,
// or "Not saved" until the next try. Structural types, so tests can drive it with stand-ins.

export type InlineState = 'saving' | 'saved' | 'error' | 'idle';

export interface InlineField extends EventTarget {
  value: string;
  blur(): void;
}

/**
 * What a save returns: 'unchanged' (nothing sent), 'saved', or an error message. The field shows
 * the state through `show`; a save already running is not started twice.
 */
export type InlineCommit = (raw: string) => Promise<'unchanged' | 'saved' | { error: string }>;

export const SAVED_TEXT = 'Saved';
export const NOT_SAVED_TEXT = 'Not saved';

export function wireInline(field: InlineField, commit: InlineCommit, show: (state: InlineState, message?: string) => void, holdMs = 1500): void {
  let running: Promise<void> | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const run = () => {
    if (running) return running;
    running = (async () => {
      if (timer !== undefined) clearTimeout(timer);
      show('saving');
      try {
        const r = await commit(field.value);
        if (r === 'unchanged') show('idle');
        else if (r === 'saved') {
          show('saved');
          timer = setTimeout(() => show('idle'), holdMs);
        } else show('error', r.error);
      } catch (e) {
        show('error', e instanceof Error ? e.message : 'Something went wrong. Try again.');
      } finally {
        running = null;
      }
    })();
    return running;
  };
  field.addEventListener('blur', () => void run());
  field.addEventListener('keydown', (e) => {
    // Enter saves by leaving the field (one path, so it never saves twice).
    if ((e as Event & { key?: string }).key === 'Enter') {
      e.preventDefault();
      field.blur();
    }
  });
}
